import { NEW_CHECKMARK_PATH, NEW_CROSS_PATH, NEW_SVG_SIZE } from './checkmark';
import { d3, shouldUpdateText, wordWrap } from './d3';
import { LARGE_FONT_SIZE, NORMAL_FONT_SIZE } from './utils';

const STROKE_WIDTH = 2;
const TEXT_BOX_HEIGHT = 60;
const DOT_PADDING = 1;
const SCORE_CIRCLE_OFFSET = 3;
export const INCORRECT_OPACITY = 0.3;
export const INCORRECT_TEXT_OPACITY = 0.7;
const SCORE_BACKGROUND_OPACITY = 0.75;
const DOTS_GROW_DURATION = 200;
const DOTS_GROW_DELAY = 300;
const DOTS_FALL_DELAY = 500;
const DOTS_FALL_DURATION = 700;
const DOTS_SHRINK_DURATION = 500;
const BUCKET_SCORE_DURATION = 200;
const BUCKET_SCORE_DELAY = 1000;
const BUCKET_CORRECT_DURATION = 200;
const LABEL_PADDING = 30;
const ALPHA_MIN = 0.04;
const ALPHA_DECAY = 0.001;
const VELOCITY_DECAY = 0.6;
const DOT_POSITION_STRENGTH = 0.1;

export interface Node {
  uniqueId: string;
  opacity: number;
  colorIndex: number;
  bucketIndex: number;
  x: number;
  y: number;
  move: boolean;
  correct_answer: boolean;
}
export interface DotsResultT {
  id: number;
  label: string;
  score: Array<number>;
  correct_answer: boolean;
}

interface Layout {
  [key: string]: Array<{ x: number; y: number }>;
}

interface T {
  bucketsLayout: Layout;
  xPos: (i: number) => number;
  yPos: (i: number) => number;
  incorrectColor: string;
  correctColor: string;
  isScoreHidden: boolean;
}

/**
 * Dots: multiple choice results represented in an animating collection of dots
 */
export class Dots implements T {
  selector = '';
  chartWidth = 0;
  chartHeight = 0;
  bucketsLayout: Layout;
  bucketWidth = 0;
  numBuckets = 0;
  enableAnimations = false;
  dotRadius = 0;
  showCorrect = false;
  showPercent = false;
  backgroundColor = '';
  textColor = '';
  totalScore = 1;
  colors: string[] = [];
  nodes: Node[] = [];
  results: DotsResultT[] = [];
  isScoreHidden;
  readonly correctColor: string;
  readonly incorrectColor: string;
  readonly xPos: (i: number) => number;
  readonly yPos: (i: number) => number;
  readonly force = d3
    .forceSimulation<Node>([])
    .alphaMin(ALPHA_MIN) // Prevent excessive ticks, which could impact performance
    .alphaDecay(ALPHA_DECAY)
    .velocityDecay(VELOCITY_DECAY); // Relationship of alpha decay to velocity decay closely resembles "friction"

  /**
   * @param bucketsLayout: map of bucket positions represented as ratios to the container size
   * @param xPos: points to a horizontal location so dots are moved to the correct bucket
   * @param yPos: points to a vertical location so dots are moved to the correct bucket
   */
  constructor(props: T) {
    this.bucketsLayout = props.bucketsLayout;
    this.xPos = props.xPos;
    this.yPos = props.yPos;
    this.correctColor = props.correctColor;
    this.incorrectColor = props.incorrectColor;
    this.isScoreHidden = props.isScoreHidden;
  }

  /**
   * Calculate the total score for all the buckets
   *
   * @returns number, total score
   */
  private getTotalScore = (results: DotsResultT[]) => {
    const scores = results.map(() => 0);
    // Replace all null or undefined scores with 0
    this.results.forEach((res, i) => {
      res.score.forEach((_, j) => {
        scores[i] = (scores[i] ?? 0) + (res.score[j] ?? 0);
      });
    });
    const total = scores.reduce((a, b) => a + b, 0);
    return total > 0 ? total : 1; // Guarantee minimum of 1
  };

  /**
   * Calculates the bucket score represented as a percent value
   *
   * @param scores: list of scores per bucket to be compared to the total score
   * @returns number, score as a percentage
   */
  private getPercentage = (
    scores: Array<number>,
    totalScore: number,
  ): number => {
    const score = scores.reduce((acc, cur) => acc + cur, 0);
    const totalNonZeroScore = totalScore || 1; // Minimum of 1
    return 100 * (score / totalNonZeroScore);
  };

  /**
   * Calculates sum of scores in the bucket
   *
   * @param scores: list of scores per bucket
   * @returns number, score
   */
  private getScore = (scores: number[]): number => {
    return scores.reduce((a, b) => a + b, 0);
  };

  /**
   * Setter for this.selector
   *
   * @param selector: string, dom node selection
   */
  public setSelector = (selector: string): void => {
    this.selector = selector;
  };

  /**
   * Setter for this.chartWidth
   *
   * @param chartWidth: number, width in pixels
   */
  public setChartWidth = (chartWidth: number): void => {
    this.chartWidth = chartWidth;
  };

  /**
   * Setter for this.chartHeight
   *
   * @param chartHeight: number, height in pixels
   */
  public setChartHeight = (chartHeight: number): void => {
    this.chartHeight = chartHeight;
  };

  /**
   * Setter for this.bucketWidth
   *
   * @param width: number, width in pixels
   */
  public setBucketWidth = (width: number): void => {
    this.bucketWidth = width;
  };

  /**
   * Setter for this.dotRadius
   *
   * @param dotRadius: number, radius in pixels
   */
  public setDotRadius = (dotRadius: number): void => {
    this.dotRadius = dotRadius;
  };

  /**
   * Setter for this.colors
   *
   * @param colors: list of colors that are used by the nodes
   */
  public setColors = (colors: string[]): void => {
    this.colors = colors;
  };

  /**
   * Setter for this.nodes
   *
   * @param nodes: list of nodes for the force simulation
   */
  public setNodes = (nodes: Node[]): void => {
    this.nodes = nodes;
  };

  /**
   * Setter for this.results
   *
   * @param results: multiple choice result data
   */
  public setResults = (results: DotsResultT[]): void => {
    this.results = results;
  };

  /**
   * Setter for this.showCorrect
   *
   * @param showCorrect: boolean
   */
  public setShowCorrect = (showCorrect: boolean): void => {
    this.showCorrect = showCorrect;
  };

  /**
   * Setter for this.showPercent
   *
   * @param showPercent: boolean
   */
  public setShowPercent = (showPercent: boolean): void => {
    this.showPercent = showPercent;
  };

  /**
   * Setter for this.numBuckets
   *
   * @param numBuckets: number
   */
  public setNumBuckets = (numBuckets: number): void => {
    this.numBuckets = numBuckets;
  };

  /**
   * Setter for this.enableAnimations
   *
   * @param enableAnimations: boolean
   */
  public setEnableAnimations = (enableAnimations: boolean): void => {
    this.enableAnimations = enableAnimations;
  };

  /**
   * Setter for this.backgroundColor
   *
   * @param backgroundColor: string, hex color
   */
  public setBackgroundColor = (backgroundColor: string): void => {
    this.backgroundColor = backgroundColor;
  };

  /**
   * Setter for this.textColor
   *
   * @param textColor: string, hex color
   */
  public setTextColor = (textColor: string): void => {
    this.textColor = textColor;
  };

  /**
   * Update force parameters to move dots from start position to appropriate bucket
   */
  public moveDotsToBuckets = (): void => {
    this.force
      .force(
        'x',
        d3
          .forceX<Node>()
          .x((d) =>
            d.move || !this.enableAnimations
              ? this.xPos(d.bucketIndex)
              : this.chartWidth,
          )
          .strength(DOT_POSITION_STRENGTH), // Keeps clusters of nodes tight, especially when entering
      )
      .force(
        'y',
        d3
          .forceY<Node>()
          .y((d) =>
            d.move || !this.enableAnimations
              ? this.yPos(d.bucketIndex)
              : this.chartHeight,
          )
          .strength(DOT_POSITION_STRENGTH),
      );
  };

  /**
   * Update the data bound to the force simulation
   */
  public updateForceNodes = (): void => {
    this.force.nodes(this.nodes);
  };

  /**
   * Change the gravitational forces that hold dots together, i.e., when changing dot size
   */
  public updateCollisions = (): void => {
    this.force.force(
      'collide',
      d3
        .forceCollide(this.dotRadius + STROKE_WIDTH + DOT_PADDING)
        .strength(1)
        .iterations(2), // Rigidity of collisions, increasing this impacts runtime
    );
  };

  /**
   * Update the total bucket score. E.g., adding a dot, changing to percentage values, or clearing results
   */
  public updateBuckets = (): void => {
    const bucketContainer = d3
      .select(this.selector)
      .selectAll('.bucket-container')
      .data(['']) // Only append one
      .join('svg')
      .attr('class', 'bucket-container')
      .attr('width', '100%')
      .attr('height', '100%');

    // Select
    const bucket = bucketContainer
      .selectAll<SVGSVGElement, DotsResultT>('.bucket')
      .data(this.results);
    // Enter
    const bucketEnter = bucket
      .enter()
      .append('svg')
      .attr('class', 'bucket')
      .style('overflow', 'visible');

    const bucketCircle = bucketEnter
      .append('circle')
      .attr('class', 'bucket-score-circle')
      .attr('cy', SCORE_CIRCLE_OFFSET)
      .attr('r', NORMAL_FONT_SIZE)
      .attr('fill-opacity', SCORE_BACKGROUND_OPACITY)
      .attr('fill', this.backgroundColor);
    const bucketText = bucketEnter
      .append('text')
      .attr('class', 'bucket-text')
      .attr('text-anchor', 'middle')
      .attr('fill', this.textColor);
    const totalScore = this.getTotalScore(this.results);
    const bucketScore = bucketEnter
      .append('text')
      .attr('class', 'bucket-score')
      .text((d) => {
        if (this.isScoreHidden) {
          return '';
        }
        return this.showPercent
          ? `${this.getPercentage(d.score, totalScore).toFixed(0)}%`
          : this.getScore(d.score);
      })
      .attr('font-size', NORMAL_FONT_SIZE)
      .attr('text-anchor', 'middle')
      .attr('font-weight', 600)
      .attr('fill', this.textColor);

    // Update
    bucket
      .merge(bucketEnter)
      .attr('x', (_, i) => {
        const bucket = this.bucketsLayout[this.numBuckets]?.[i];
        return bucket !== undefined
          ? this.chartWidth * bucket.x
          : this.chartWidth;
      })
      .attr('y', (_, i) => {
        const bucket = this.bucketsLayout[this.numBuckets]?.[i];
        return bucket ? this.chartHeight * bucket.y : this.chartHeight;
      })
      .attr('width', this.bucketWidth)
      .attr('height', this.bucketWidth);
    const bucketCircleSelection = bucket
      .select<SVGCircleElement>('.bucket-score-circle')
      .merge(bucketCircle)
      .attr('fill', this.backgroundColor);

    const bucketTextSelection = bucket
      .select<SVGTextElement>('.bucket-text')
      .merge(bucketText)
      .attr('transform', `translate(0, ${NORMAL_FONT_SIZE + LABEL_PADDING})`)
      .attr('opacity', (d) =>
        !this.showCorrect || d.correct_answer ? 1 : INCORRECT_TEXT_OPACITY,
      )
      .attr('fill', this.textColor);

    // Correct answers
    const bucketCorrectAnswers = bucketEnter
      .append('path')
      .attr('class', 'correct-answer-icon')
      .attr('opacity', 0);

    const correctAnswerIcons = bucket
      .select<SVGPathElement>('.correct-answer-icon')
      .merge(bucketCorrectAnswers);

    correctAnswerIcons
      .attr('d', (d) =>
        d.correct_answer ? NEW_CHECKMARK_PATH : NEW_CROSS_PATH,
      )
      .attr('fill', (d) =>
        d.correct_answer ? this.correctColor : this.incorrectColor,
      )
      .attr('transform', (_d, i) => {
        const labelNode = bucketTextSelection.nodes()[i] as SVGGraphicsElement;
        const labelHeight = labelNode?.getBBox().height || NORMAL_FONT_SIZE;
        const circlesNode = bucketCircleSelection.nodes()[
          i
        ] as SVGGraphicsElement;
        const circlesHeight = circlesNode?.getBBox().height || NORMAL_FONT_SIZE;
        return `translate(-${NEW_SVG_SIZE / 2}, ${
          labelHeight + circlesHeight
        })`;
      });

    // Transition
    correctAnswerIcons
      .conditionalTransition({
        duration: this.enableAnimations ? BUCKET_CORRECT_DURATION : 0,
        ease: d3.easeExp,
        name: 'correct-answer-transition',
      })
      .attr('opacity', this.showCorrect ? 1 : 0)
      .end();

    if (
      shouldUpdateText({
        selection: bucketTextSelection,
        texts: bucketTextSelection.data().map((d) => d.label),
        maxWidth: this.bucketWidth,
        maxHeight: TEXT_BOX_HEIGHT,
      })
    ) {
      bucketTextSelection
        .text((d) => d.label)
        .call(wordWrap, {
          maxWidth: this.bucketWidth,
          maxHeight: TEXT_BOX_HEIGHT,
          fontSize: LARGE_FONT_SIZE,
        });
    }

    bucket
      .select<SVGTextElement>('.bucket-score')
      .merge(bucketScore)
      .attr('transform', `translate(0, ${NORMAL_FONT_SIZE / 2})`)
      .attr('opacity', (d) =>
        !this.showCorrect || d.correct_answer ? 1 : INCORRECT_TEXT_OPACITY,
      )
      .transition()
      .duration(this.enableAnimations ? BUCKET_SCORE_DURATION : 0)
      .delay(this.enableAnimations ? BUCKET_SCORE_DELAY : 0)
      .text((d) => {
        if (this.isScoreHidden) {
          return '';
        }
        return this.showPercent
          ? `${this.getPercentage(d.score, totalScore).toFixed(0)}%`
          : this.getScore(d.score);
      })
      .attr('fill', this.textColor);

    // Exit
    bucket.exit().remove();
  };

  /**
   * Update the dots' positions, size, color, etc. and re-heat the force simulation
   */
  public updateDots = (): void => {
    // Select
    const dots = d3
      .select(this.selector)
      .selectAll<SVGCircleElement, Node>('.dot-item')
      .data(this.nodes, (d: Node) => d.uniqueId);
    // Enter
    const dotsEnter = dots
      .enter()
      .insert('circle', ':first-child')
      .attr('class', 'dot-item')
      .attr('r', this.enableAnimations ? 0 : this.dotRadius)
      .attr('fill', (d) => this.colors[d.colorIndex] || null)
      .attr('fill-opacity', (d) => d.opacity)
      .attr('stroke', (d) => this.colors[d.colorIndex] || null)
      .attr('stroke-width', STROKE_WIDTH);

    // Update
    dots
      .merge(dotsEnter)
      .attr('opacity', (d) =>
        this.showCorrect && !d.correct_answer ? INCORRECT_OPACITY : 1,
      )
      .transition()
      .duration(this.enableAnimations ? DOTS_GROW_DURATION : 0)
      .delay(() =>
        this.enableAnimations ? Math.random() * DOTS_GROW_DELAY : 0,
      )
      .attr('r', this.dotRadius)
      .attr('fill', (d) => this.colors[d.colorIndex] || null)
      .attr('fill-opacity', (d) => d.opacity)
      .attr('stroke', (d) => this.colors[d.colorIndex] || null);
    // Enable move to bucket after enter (NB: not merged with enter)
    dots.each((d) => (d.move = true));
    const tick = () => {
      dots
        .merge(dotsEnter)
        .attr('cx', (d) => d.x)
        .attr('cy', (d) => d.y - LABEL_PADDING);
    };
    // Exit
    dots
      .exit()
      .transition()
      .delay(() =>
        this.enableAnimations ? Math.random() * DOTS_FALL_DELAY : 0,
      )
      .duration(this.enableAnimations ? DOTS_FALL_DURATION : 0)
      .ease(d3.easeBounce)
      .attr('cy', this.chartHeight)
      .transition()
      .duration(this.enableAnimations ? DOTS_SHRINK_DURATION : 0)
      .attr('r', 0)
      .remove();

    this.force
      .restart()
      .alpha(1) // "Re-heat" the simulation to prevent charge decay
      .on('tick', tick);
  };

  /**
   * Visualization updater
   *
   * @remarks
   *  Rather than waiting for animation code to finish running, we need to chain async actions
   *  around the time when the force simulation settles. As a rough heuristic, this method can be
   *  run after a fixed timeout, like 1000ms.
   *
   * @returns a resolved promise
   */
  public update = (): Promise<void> => {
    return Promise.resolve();
  };
}
